import { Building2, Calendar, FileText, CalendarX, Trash2, AlertCircle } from "lucide-react";
import { formatDate } from "@/utils/dateFormatters";
import { formatEuro, safeParseDate } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { isValidDateString } from "@/utils/dateValidation";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

interface WorkTenderCardProps {
  tender: any;
  onClick: () => void;
  onDelete?: () => void;
}

export const WorkTenderCard = ({
  tender,
  onClick,
  onDelete
}: WorkTenderCardProps) => {
  const {
    t,
    i18n
  } = useTranslation("tenderManagement");
  const isDanish = i18n.language === "da";

  const {
    data: documents = [],
    isLoading: isLoadingDocuments
  } = useQuery({
    queryKey: ['tender-documents', tender.id],
    queryFn: async () => {
      const {
        data,
        error
      } = await supabase.from('tender_documents').select('*').eq('tender_id', tender.id);
      if (error) throw error;
      return data || [];
    }
  });

  const milestones = [
    { name: t("milestones.tenderPublished") },
    { name: t("milestones.bidDeadline") }
  ];

  const getTenderTitle = (tender: any) => {
    if (tender.tender_data && tender.tender_data.title) {
      return tender.tender_data.title;
    }
    if (!tender || !tender.tender_data && !tender.title) {
      return "Untitled Tender";
    }
    if (tender.tender_data) {
      if (tender.tender_data.match_reasons?.["notice-title"]?.eng) {
        return tender.tender_data.match_reasons["notice-title"].eng;
      }
      if (tender.tender_data.match_reasons?.notice_title?.eng?.[0]) {
        return tender.tender_data.match_reasons.notice_title.eng[0];
      }
      if (tender.tender_data["notice-title"]?.eng) {
        return tender.tender_data["notice-title"].eng;
      }
      if (tender.tender_data.TI?.eng) {
        return tender.tender_data.TI.eng;
      }
      if (tender.tender_data["notice-title"]) {
        const languages = Object.keys(tender.tender_data["notice-title"]);
        if (languages.length > 0) {
          return tender.tender_data["notice-title"][languages[0]];
        }
      }
      if (tender.tender_data.match_reasons?.["notice-title"]) {
        const languages = Object.keys(tender.tender_data.match_reasons["notice-title"]);
        if (languages.length > 0) {
          const titleValue = tender.tender_data.match_reasons["notice-title"][languages[0]];
          return Array.isArray(titleValue) ? titleValue[0] : titleValue;
        }
      }
    }
    if (tender.title) {
      return tender.title;
    }
    return "Untitled Tender";
  };

  const getOrganization = (tender: any) => {
    if (tender.tender_data && tender.tender_data["organisation-name-buyer"]) {
      const orgNameBuyer = tender.tender_data["organisation-name-buyer"];
      if (typeof orgNameBuyer === 'object') {
        const langs = Object.keys(orgNameBuyer);
        if (langs.length > 0) {
          const orgValue = orgNameBuyer[langs[0]];
          if (Array.isArray(orgValue) && orgValue.length > 0) {
            return orgValue[0];
          } else if (typeof orgValue === 'string') {
            return orgValue;
          }
        }
      } else if (Array.isArray(orgNameBuyer) && orgNameBuyer.length > 0) {
        return orgNameBuyer[0];
      } else if (typeof orgNameBuyer === 'string') {
        return orgNameBuyer;
      }
    }
    if (tender.tender_data && tender.tender_data.organization_name) {
      return tender.tender_data.organization_name;
    }
    if (tender.tender_data) {
      if (tender.tender_data.match_reasons) {
        if (tender.tender_data.match_reasons["buyer-name"]?.eng?.[0]) {
          return tender.tender_data.match_reasons["buyer-name"].eng[0];
        }
        const buyerNameObj = tender.tender_data.match_reasons["buyer-name"];
        if (buyerNameObj) {
          const langs = Object.keys(buyerNameObj);
          if (langs.length > 0) {
            const firstLang = buyerNameObj[langs[0]];
            if (Array.isArray(firstLang) && firstLang.length > 0) {
              return firstLang[0];
            } else if (typeof firstLang === 'string') {
              return firstLang;
            }
          }
        }
      }
      if (tender.tender_data["buyer-name"]) {
        const buyerNameObj = tender.tender_data["buyer-name"];
        if (typeof buyerNameObj === 'object') {
          if (buyerNameObj.eng && Array.isArray(buyerNameObj.eng) && buyerNameObj.eng.length > 0) {
            return buyerNameObj.eng[0];
          }
          const langs = Object.keys(buyerNameObj);
          if (langs.length > 0) {
            const firstLang = buyerNameObj[langs[0]];
            if (Array.isArray(firstLang) && firstLang.length > 0) {
              return firstLang[0];
            } else if (typeof firstLang === 'string') {
              return firstLang;
            }
          }
        } else if (typeof buyerNameObj === 'string') {
          return buyerNameObj;
        }
      }
      if (tender.tender_data.AU) {
        if (typeof tender.tender_data.AU === 'object') {
          const langs = Object.keys(tender.tender_data.AU);
          if (langs.length > 0 && Array.isArray(tender.tender_data.AU[langs[0]])) {
            return tender.tender_data.AU[langs[0]][0] || "Unknown Organization";
          }
        }
        return tender.tender_data.AU;
      }
    }
    return tender.organization_name || "Unknown Organization";
  };

  const getDeadline = (tender: any) => {
    let deadline = null;
    
    try {
      if (tender.tender_data) {
        // Check multiple possible deadline fields
        deadline = 
          tender.tender_data.deadline_date || 
          tender.tender_data["deadline-receipt-tender-date-lot"]?.[0] || 
          tender.tender_data.match_reasons?.["deadline-receipt-tender-date-lot"]?.[0] || 
          tender.tender_data["deadline-receipt-request"]?.[0] ||
          tender.tender_data.DT || 
          (tender.tender_data["DT-APPLICATIONS"] ? tender.tender_data["DT-APPLICATIONS"] : null);

        // Handle nested deadline structure
        if (!deadline && tender.tender_data.deadline && tender.tender_data.deadline.date) {
          deadline = tender.tender_data.deadline.date;
        }
      } else {
        deadline = tender.deadline_date || tender.deadline;
      }
      
      if (!deadline) {
        console.log("No deadline found for tender:", tender.id || "unknown");
        return null;
      }
      
      // Log the deadline for debugging
      console.log("Raw deadline found:", deadline, "for tender:", tender.id || "unknown");
      
      // Check if deadline is a valid date string before trying to parse it
      if (!isValidDateString(deadline)) {
        console.warn("Invalid deadline format:", deadline);
        return null;
      }
      
      // Use our improved safeParseDate function to handle various date formats
      return safeParseDate(deadline);
    } catch (error) {
      console.error("Error parsing deadline:", error, "for tender:", tender.id || "unknown");
      return null;
    }
  };

  const getValue = (tender: any) => {
    if (tender.tender_data && tender.tender_data.value && tender.tender_data.value !== "N/A" && tender.tender_data.value !== "Value not specified") {
      return tender.tender_data.value;
    }
    if (tender.tender_data) {
      if (tender.tender_data["estimated-value-lot"] && tender.tender_data["estimated-value-cur-lot"]) {
        const value = tender.tender_data["estimated-value-lot"][0];
        const currency = tender.tender_data["estimated-value-cur-lot"][0];
        if (value && currency) {
          return formatEuro(value.toString());
        }
      }
      if (tender.tender_data.TV && tender.tender_data.TV_CUR) {
        return `${formatEuro(tender.tender_data.TV.toString())} ${tender.tender_data.TV_CUR}`;
      }
      const value = tender.tender_data.value;
      if (value && value !== "N/A") {
        if (typeof value === 'string' && value.includes(' ')) {
          const [amount, currency] = value.toString().split(' ');
          const formattedAmount = parseFloat(amount);
          if (!isNaN(formattedAmount)) {
            return formatEuro(formattedAmount.toString());
          }
        }
        return value;
      }
    }
    if (tender.value && tender.value !== "N/A") {
      return tender.value;
    }
    return "Value not specified";
  };

  // Helper function to safely check the deadline status
  function getDeadlineStatus(deadline: Date | null) {
    if (!deadline) return null;
    
    try {
      // Ensure we have a valid date before proceeding
      if (isNaN(deadline.getTime())) {
        console.warn("Invalid deadline date detected");
        return null;
      }
      
      const now = new Date();
      
      const deadlineDate = new Date(deadline);
      deadlineDate.setHours(0, 0, 0, 0);
      
      const todayDate = new Date(now);
      todayDate.setHours(0, 0, 0, 0);
      
      const daysLeft = Math.ceil((deadline.getTime() - now.getTime()) / (1000 * 3600 * 24));
      
      const isPast = todayDate > deadlineDate;
      const isToday = todayDate.getTime() === deadlineDate.getTime();
      
      return {
        isPast,
        isToday,
        daysLeft
      };
    } catch (error) {
      console.error("Error calculating deadline status:", error);
      return null;
    }
  }

  // Safely get the deadline and status
  const deadline = getDeadline(tender);
  const deadlineStatus = deadline ? getDeadlineStatus(deadline) : null;
  const daysUntilDeadline = deadlineStatus ? deadlineStatus.daysLeft : null;

  const documentCount = Array.isArray(documents) ? documents.length : 0;

  if (!tender) {
    return null;
  }

  // Format the deadline date safely for display
  const formattedDeadline = deadline && isValidDate(deadline) ? formatDate(deadline.toISOString(), isDanish) : 
    isDanish ? "Ingen deadline angivet" : "No deadline specified";
  
  // Helper function to check if a date is valid
  function isValidDate(date: Date | null): boolean {
    return date !== null && !isNaN(date.getTime());
  }

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      console.log('Starting deletion process for tender:', tender.id);

      const { data: tenderDocs, error: tenderDocsError } = await supabase
        .from('tender_documents')
        .select('id, file_path')
        .eq('tender_id', tender.id);

      if (tenderDocsError) {
        console.error('Error fetching tender documents:', tenderDocsError);
      } else if (tenderDocs && tenderDocs.length > 0) {
        // Delete storage files for tender documents
        for (const doc of tenderDocs) {
          if (doc.file_path) {
            const { error: storageError } = await supabase.storage
              .from('tender_documents')
              .remove([doc.file_path]);
              
            if (storageError) {
              console.warn('Error deleting document file from storage:', storageError);
            }
          }
        }
      }

      const { error: tenderError } = await supabase
        .from('saved_tenders')
        .delete()
        .eq('id', tender.id);

      if (tenderError) {
        throw tenderError;
      }

      if (onDelete) {
        onDelete();
      }
    } catch (error) {
      console.error('Error in deletion process:', error);
    }
  };

  return (
    <Card className={cn(
      "hover:bg-gray-50 cursor-pointer transition-colors bg-white shadow-sm border",
      deadlineStatus?.isPast ? "border-red-200 bg-red-50/50" : "border-gray-100"
    )} onClick={onClick}>
      <div className="space-y-4 p-6">
        <div className="flex justify-between items-start gap-4">
          <h3 className="text-lg font-semibold text-gray-900 leading-tight line-clamp-2 break-words max-w-[70%]">
            {getTenderTitle(tender)}
          </h3>
          <div className="flex items-center gap-2">
            {deadlineStatus?.isToday ? (
              <Badge variant="secondary" className="whitespace-nowrap">
                {t("daysLeft", { count: 0 })}
              </Badge>
            ) : deadlineStatus?.isPast ? (
              <Badge variant="destructive" className="whitespace-nowrap">
                <CalendarX className="h-3 w-3 mr-1" />
                {t("common.deadlinePassed")}
              </Badge>
            ) : (
              <Badge 
                variant={daysUntilDeadline && daysUntilDeadline < 7 ? "destructive" : "secondary"}
                className="whitespace-nowrap"
              >
                {daysUntilDeadline ? t("daysLeft", {
                  count: daysUntilDeadline
                }) : t("noDeadline")}
              </Badge>
            )}
            {onDelete && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex items-center gap-1 text-red-600 hover:text-red-700"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent onClick={(e) => e.stopPropagation()}>
                  <AlertDialogHeader>
                    <AlertDialogTitle className="flex items-center gap-2">
                      <AlertCircle className="h-5 w-5 text-red-600" />
                      {t("deleteDialog.title")}
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      {t("deleteDialog.description")}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel onClick={(e) => e.stopPropagation()}>
                      {t("deleteDialog.cancel")}
                    </AlertDialogCancel>
                    <AlertDialogAction onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(e);
                    }} className="bg-red-600 hover:bg-red-700">
                      {t("deleteDialog.confirm")}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center gap-2 text-gray-600 min-w-0">
            <Building2 className="h-4 w-4 flex-shrink-0" />
            <span className="text-sm truncate">{getOrganization(tender)}</span>
          </div>
          
          <div className="flex items-center gap-2 text-gray-600 min-w-0">
            <Calendar className="h-4 w-4 flex-shrink-0" />
            <span className="text-sm truncate">
              {formattedDeadline}
            </span>
          </div>

          <div className="flex items-center gap-2 min-w-0">
            <span className="text-sm font-medium text-gray-900 truncate">{getValue(tender)}</span>
          </div>
        </div>

        <div className="pt-4 border-t border-gray-100">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="h-4 w-4 text-gray-500 flex-shrink-0" />
            <span className="text-sm text-gray-600 truncate">
              {t("documents.count", { count: documentCount })}
            </span>
          </div>
          
          {deadline && daysUntilDeadline && daysUntilDeadline > 0 && (
            <div className="space-y-2">
              <div className="relative pt-8">
                <Progress value={Math.max(0, Math.min(100, (30 - daysUntilDeadline) / 30 * 100))} className="h-2 bg-gray-100" />
                <div className="absolute top-0 left-0 right-0 flex justify-between">
                  {milestones.map((milestone, index) => (
                    <div key={index} className="flex flex-col items-center">
                      <span className="text-xs text-gray-600 truncate max-w-[120px]">{milestone.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};
